import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Building2 } from 'lucide-react';

const ClientFormModal = ({
  open,
  onClose,
  mode, // 'create' or 'edit'
  formData,
  onFormChange,
  onSubmit
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const handleChange = (field, value) => {
    onFormChange({ ...formData, [field]: value });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {mode === 'create' ? 'Add New Client' : 'Edit Client'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Company Name"
                required
                data-testid="client-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="short_name">Short Name</Label>
              <Input
                id="short_name"
                value={formData.short_name || ''}
                onChange={(e) => handleChange('short_name', e.target.value)}
                placeholder="Abbreviation"
                data-testid="client-short-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="pan">PAN</Label>
              <Input
                id="pan"
                value={formData.pan || ''}
                onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                placeholder="AAAAA0000A"
                maxLength={10}
                data-testid="client-pan-input"
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="gstin">GSTIN</Label>
              <Input
                id="gstin"
                value={formData.gstin || ''}
                onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                placeholder="00AAAAA0000A0Z0"
                maxLength={15}
                data-testid="client-gstin-input"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Contact Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person || ''}
                  onChange={(e) => handleChange('contact_person', e.target.value)}
                  placeholder="John Doe"
                  data-testid="client-contact-input"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  data-testid="client-phone-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="contact@company.com"
                  data-testid="client-email-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Full address"
                  data-testid="client-address-input"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Tally Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tally_host">Tally Host</Label>
                <Input
                  id="tally_host"
                  value={formData.tally_host || 'localhost'}
                  onChange={(e) => handleChange('tally_host', e.target.value)}
                  placeholder="localhost"
                  data-testid="client-tally-host-input"
                />
              </div>
              
              <div>
                <Label htmlFor="tally_port">Tally Port</Label>
                <Input
                  id="tally_port"
                  type="number"
                  value={formData.tally_port || 9000}
                  onChange={(e) => handleChange('tally_port', parseInt(e.target.value) || 9000)}
                  placeholder="9000"
                  data-testid="client-tally-port-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="tally_company">Tally Company Name</Label>
                <Input
                  id="tally_company"
                  value={formData.tally_company || ''}
                  onChange={(e) => handleChange('tally_company', e.target.value)}
                  placeholder="Company name as in Tally"
                  data-testid="client-tally-company-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" data-testid="submit-client-btn">
              {mode === 'create' ? 'Create Client' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientFormModal;
